import React, { useEffect, useState } from 'react';
import { Radio, Users, TrainFront, AlertTriangle, Info } from 'lucide-react';
import { getLiveLocal } from '@/lib/api';

const CROWD_META = {
  low:    { label: 'Quiet',   color: 'text-[#2F8F5A]', dot: 'bg-[#2F8F5A]' },
  medium: { label: 'Busy',    color: 'text-[#B85F31]', dot: 'bg-[#F2A47A]' },
  high:   { label: 'Packed',  color: 'text-[#B91C2E]', dot: 'bg-[#E88A5F]' },
};

const SEV = {
  alert: { icon: AlertTriangle, className: 'text-[#B91C2E]' },
  warn:  { icon: AlertTriangle, className: 'text-[#B85F31]' },
  info:  { icon: Info,          className: 'text-[#15291C]' },
};

export default function LiveLocalInfoCard({ lat, lon }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof lat !== 'number' || typeof lon !== 'number') { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    getLiveLocal(lat, lon)
      .then(d => alive && setData(d))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [lat, lon]);

  if (loading) return <div className="puvio-card p-5 gt-skeleton h-32" data-testid="live-local-skeleton" />;
  if (!data) return null;

  const crowd = CROWD_META[data.crowd] || CROWD_META.low;
  const firstAlert = (data.alerts || [])[0];
  const SevIcon = SEV[firstAlert?.severity || 'info']?.icon || Info;
  const sevClass = SEV[firstAlert?.severity || 'info']?.className || 'text-[#15291C]';

  return (
    <div className="puvio-card p-5" data-testid="live-local-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-[#C4FF3D]">
            <Radio className="w-3.5 h-3.5 text-[#15291C]" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#B91C2E] animate-pulse" />
          </span>
          <p className="font-bold text-[10.5px] uppercase tracking-[0.22em] text-[#15291C]">Live right now</p>
        </div>
        <span className={`gt-pill gt-pill-cream text-[10.5px] font-bold ${crowd.color}`} data-testid="live-local-crowd">
          <span className={`w-1.5 h-1.5 rounded-full ${crowd.dot}`} /> {crowd.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-2xl bg-[#F2F0E4] border border-[#0F1A13]/05 p-3" data-testid="live-local-transit">
          <TrainFront className="w-4 h-4 text-[#15291C]" />
          <p className="text-[10.5px] uppercase tracking-wide text-[#7A8A80] font-semibold mt-1.5">Transit</p>
          <p className="font-display text-lg font-bold text-[#0F1A13] leading-none mt-0.5">
            {data.transit?.covered ? data.transit.routes_count : '—'}
            {data.transit?.covered && <span className="text-[10px] text-[#7A8A80] font-medium ml-1">lines</span>}
          </p>
        </div>
        <div className="rounded-2xl bg-[#F2F0E4] border border-[#0F1A13]/05 p-3" data-testid="live-local-crowd-panel">
          <Users className="w-4 h-4 text-[#15291C]" />
          <p className="text-[10.5px] uppercase tracking-wide text-[#7A8A80] font-semibold mt-1.5">Crowd</p>
          <p className={`font-display text-lg font-bold leading-none mt-0.5 ${crowd.color}`}>{crowd.label}</p>
        </div>
        <div className="rounded-2xl bg-[#F2F0E4] border border-[#0F1A13]/05 p-3" data-testid="live-local-alerts-count">
          <AlertTriangle className="w-4 h-4 text-[#B85F31]" />
          <p className="text-[10.5px] uppercase tracking-wide text-[#7A8A80] font-semibold mt-1.5">Alerts</p>
          <p className="font-display text-lg font-bold text-[#0F1A13] leading-none mt-0.5">
            {(data.alerts || []).length}
          </p>
        </div>
      </div>

      {firstAlert && (
        <div className="mt-3 rounded-2xl bg-[#FFFBEC] border border-[#F2A47A]/30 p-3 flex items-start gap-2.5" data-testid="live-local-top-alert">
          <SevIcon className={`w-4 h-4 ${sevClass} shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-[#0F1A13]">{firstAlert.title}</p>
            <p className="text-[12px] text-[#4A5A50] mt-0.5 leading-snug">{firstAlert.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}
